import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { useDataManager } from '../../hooks/useDataManager';
import { Check, Plus, Trash2 } from 'lucide-react';

const HeroManagement = () => {
  const { heroSlides, addHeroSlide, updateHeroSlide, deleteHeroSlide } = useDataManager();
  const [editedSlides, setEditedSlides] = useState({});
  const [savedStates, setSavedStates] = useState({});

  // Initialize edited slides from heroSlides
  useEffect(() => {
    const initialSlides = {};
    heroSlides.forEach(slide => {
      initialSlides[slide.id] = { ...slide };
    });
    setEditedSlides(initialSlides);
  }, [heroSlides]);

  const handleChange = (id, field, value) => {
    setEditedSlides(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleSave = (id) => {
    const slideToSave = editedSlides[id];
    updateHeroSlide(id, slideToSave);

    // Show saved feedback
    setSavedStates(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setSavedStates(prev => ({ ...prev, [id]: false }));
    }, 2000);
  };

  const hasChanges = (id) => {
    const original = heroSlides.find(s => s.id === id);
    const edited = editedSlides[id];
    if (!original || !edited) return false;
    return JSON.stringify(original) !== JSON.stringify(edited);
  };

  const handleAddNew = () => {
    const newSlide = {
      title: '',
      subtitle: '',
      description: '',
      image: '',
      buttonText: 'Shop Now',
      buttonLink: '#products',
      active: true
    };
    addHeroSlide(newSlide);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      deleteHeroSlide(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Hero Banners</h2>
          <p className="text-gray-600 mt-1">Manage homepage carousel banners</p>
        </div>
        <Button
          onClick={handleAddNew}
          className="bg-[#4a1a1f] hover:bg-[#5a2a2f] text-white"
        >
          <Plus size={18} className="mr-2" />
          Add New Banner
        </Button>
      </div>

      <div className="grid gap-6">
        {heroSlides.map((slide, index) => {
          const editedSlide = editedSlides[slide.id] || slide;
          const isSaved = savedStates[slide.id];
          const changed = hasChanges(slide.id);

          return (
            <Card key={slide.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <h3 className="font-semibold">Banner {index + 1}</h3>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editedSlide.active !== false}
                        onCheckedChange={(checked) => handleChange(slide.id, 'active', checked)}
                      />
                      <Label className="text-sm text-gray-600">
                        {editedSlide.active !== false ? 'Active' : 'Inactive'}
                      </Label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleSave(slide.id)}
                      disabled={!changed || isSaved}
                      className={`${
                        isSaved
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-[#4a1a1f] hover:bg-[#5a2a2f]'
                      } ${!changed ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isSaved ? (
                        <>
                          <Check size={16} className="mr-2" />
                          Saved!
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                    <Button
                      onClick={() => handleDelete(slide.id)}
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={editedSlide.title || ''}
                      onChange={(e) => handleChange(slide.id, 'title', e.target.value)}
                      placeholder="SUMMER COLLECTION"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={editedSlide.subtitle || ''}
                      onChange={(e) => handleChange(slide.id, 'subtitle', e.target.value)}
                      placeholder="Flat 50% Off"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Input
                      value={editedSlide.description || ''}
                      onChange={(e) => handleChange(slide.id, 'description', e.target.value)}
                      placeholder="Discover our elegant abaya collection"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Image URL</Label>
                    <Input
                      value={editedSlide.image || ''}
                      onChange={(e) => handleChange(slide.id, 'image', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label>Button Text</Label>
                    <Input
                      value={editedSlide.buttonText || ''}
                      onChange={(e) => handleChange(slide.id, 'buttonText', e.target.value)}
                      placeholder="Shop Now"
                    />
                  </div>
                  <div>
                    <Label>Button Link</Label>
                    <Input
                      value={editedSlide.buttonLink || ''}
                      onChange={(e) => handleChange(slide.id, 'buttonLink', e.target.value)}
                      placeholder="#products"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default HeroManagement;
